import { useEffect, useMemo } from "react";
import type { Key } from "react-aria-components";
import { useListData } from "react-stately";
import { Input } from "@/components/base/input/input";
import { MultiSelect } from "@/components/base/select/multi-select";
import type { SelectItemType } from "@/components/base/select/select";
import { NativeSelect } from "@/components/base/select/select-native";
import type { FormRuleFieldOption } from "./form-rule-condition-value.util";
import { toSelectItemTypes } from "./form-rule-condition-value.util";

const EMPTY_SELECT_VALUE = "";

interface FormRuleOptionSingleValueInputProps {
	label: string;
	value: string;
	options: FormRuleFieldOption[];
	onChange: (value: string) => void;
	onBlur?: () => void;
}

/**
 * Returns the stored value when it matches a field option; otherwise empty (unset).
 */
function resolveSingleSelectValue(
	value: string,
	options: FormRuleFieldOption[],
): string {
	if (!value) {
		return EMPTY_SELECT_VALUE;
	}
	return options.some((opt) => opt.value === value)
		? value
		: EMPTY_SELECT_VALUE;
}

/**
 * Single-value picker for select/radio field options (equals, not equals, etc.).
 */
export function FormRuleOptionSingleValueInput({
	label,
	value,
	options,
	onChange,
	onBlur,
}: FormRuleOptionSingleValueInputProps) {
	const selectOptions = useMemo(
		() => [
			{
				value: EMPTY_SELECT_VALUE,
				label: "",
				disabled: true,
				hidden: true,
			},
			...options.map((opt) => ({ value: opt.value, label: opt.label })),
		],
		[options],
	);

	const resolvedValue = resolveSingleSelectValue(value, options);

	return (
		<NativeSelect
			className="w-full"
			label={label}
			value={resolvedValue}
			onChange={(e) => onChange(e.target.value)}
			onBlur={onBlur}
			options={selectOptions}
			selectClassName="py-2 text-sm"
		/>
	);
}

interface FormRuleOptionMultiValueInputProps {
	label: string;
	value: string[];
	options: FormRuleFieldOption[];
	placeholder: string;
	onChange: (value: string[]) => void;
	onBlur?: () => void;
}

/**
 * Multi-value picker for select fields with `in` / `not_in` operators.
 * Uses the design-system MultiSelect with removable chip tags.
 */
export function FormRuleOptionMultiValueInput({
	label,
	value,
	options,
	placeholder,
	onChange,
	onBlur,
}: FormRuleOptionMultiValueInputProps) {
	const selectItems = useMemo(() => toSelectItemTypes(options), [options]);

	const selectedItems = useListData<SelectItemType>({
		initialItems: [],
	});

	useEffect(() => {
		const desired = selectItems.filter((item) => value.includes(item.id));
		const currentIds = selectedItems.items.map((item) => item.id).join("\0");
		const desiredIds = desired.map((item) => item.id).join("\0");
		if (currentIds === desiredIds) {
			return;
		}

		for (const item of [...selectedItems.items]) {
			selectedItems.remove(item.id);
		}
		for (const item of desired) {
			selectedItems.append(item);
		}
	}, [value, selectItems, selectedItems]);

	const handleItemInserted = (key: Key) => {
		const next = String(key);
		if (value.includes(next)) {
			return;
		}
		onChange([...value, next]);
	};

	const handleItemCleared = (key: Key) => {
		onChange(value.filter((entry) => entry !== String(key)));
	};

	return (
		<MultiSelect
			label={label}
			items={selectItems}
			selectedItems={selectedItems}
			placeholder={placeholder}
			placeholderIcon={null}
			showTagAvatar={false}
			onItemInserted={handleItemInserted}
			onItemCleared={handleItemCleared}
			onBlur={onBlur}
		>
			{(item) => (
				<MultiSelect.Item id={item.id} isDisabled={item.isDisabled}>
					{item.label}
				</MultiSelect.Item>
			)}
		</MultiSelect>
	);
}

interface FormRuleTextValueInputProps {
	label: string;
	value: string;
	placeholder: string;
	onChange: (value: string) => void;
	onBlur?: () => void;
}

/**
 * Fallback text input for field types without predefined options.
 */
export function FormRuleTextValueInput({
	label,
	value,
	placeholder,
	onChange,
	onBlur,
}: FormRuleTextValueInputProps) {
	return (
		<Input
			className="w-full"
			label={label}
			size="sm"
			value={value}
			onChange={(next) => {
				const raw =
					typeof next === "string"
						? next
						: ((next as { target: { value: string } }).target?.value ?? "");
				onChange(raw);
			}}
			onBlur={onBlur}
			placeholder={placeholder}
		/>
	);
}
